import React, { useState } from 'react';
import api from '../../services/api';

// ✅ Composant partagé — utilisé par AssociationDetails.jsx (Admin + Owner)
// ET SimpleDashboard.jsx (Simple). Seul `periodsBasePath` change selon le rôle :
//   Admin/Owner : /associations/:id/schedule-periods
//   Simple      : /simple-association/schedule-periods
//
// Design : cartes horaires colorées par moment de la journée + mini-timeline
// 24h en un coup d'œil + formulaire d'ajout minimaliste (nom + 2 heures).

const PERIOD_STYLES = [
  { maxHour: 5, gradient: 'from-indigo-500 to-slate-600', bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', dot: 'bg-indigo-500', icon: '🌙' },
  { maxHour: 12, gradient: 'from-amber-400 to-orange-500', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500', icon: '☀️' },
  { maxHour: 18, gradient: 'from-sky-400 to-blue-500', bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', dot: 'bg-sky-500', icon: '🌤️' },
  { maxHour: 24, gradient: 'from-purple-500 to-fuchsia-600', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500', icon: '🌆' },
];

function styleForTime(onTime) {
  const hour = parseInt(onTime?.split(':')[0] ?? '0', 10);
  return PERIOD_STYLES.find((s) => hour < s.maxHour) || PERIOD_STYLES[PERIOD_STYLES.length - 1];
}

function timeToPercent(time) {
  const [h, m] = time.split(':').map(Number);
  return ((h * 60 + m) / 1440) * 100;
}

// Segments pour la timeline — une période qui chevauche minuit devient 2 blocs
function periodToTimelineSegments(period) {
  const startPct = timeToPercent(period.onTime);
  const endPct = timeToPercent(period.offTime);
  if (endPct > startPct) return [{ left: startPct, width: endPct - startPct }];
  return [
    { left: startPct, width: 100 - startPct },
    { left: 0, width: endPct },
  ];
}

const emptyForm = { name: '', onTime: '08:00', offTime: '18:00' };

const SchedulePeriodsManager = ({
  scheduleEnabled,
  onToggleEnabled,
  periods = [],
  legacyOnTime,
  legacyOffTime,
  periodsBasePath,
  onPeriodsChange,
  readOnly = false,
}) => {
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [togglingEnabled, setTogglingEnabled] = useState(false);

  // ✅ Compat : si aucune période réelle n'existe encore mais qu'un horaire
  // legacy est défini, on l'affiche comme une carte normale — l'éditer crée
  // la première vraie période en base, de façon transparente pour l'utilisateur.
  const displayPeriods = periods.length > 0
    ? periods
    : (legacyOnTime && legacyOffTime)
      ? [{ _id: null, name: 'Programmation', onTime: legacyOnTime, offTime: legacyOffTime, enabled: true, isLegacy: true }]
      : [];

  const openAddForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setFormOpen(true);
  };

  const openEditForm = (period) => {
    setEditingId(period.isLegacy ? null : period._id);
    setForm({ name: period.isLegacy ? '' : period.name, onTime: period.onTime, offTime: period.offTime });
    setError('');
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  };

  const handleToggleEnabled = async () => {
    setTogglingEnabled(true);
    try {
      await onToggleEnabled(!scheduleEnabled);
    } finally {
      setTogglingEnabled(false);
    }
  };

  const handleSave = async () => {
    setError('');

    if (!form.name.trim()) {
      setError('Le nom de la période est requis');
      return;
    }
    if (form.onTime === form.offTime) {
      setError("L'heure ON et l'heure OFF ne peuvent pas être identiques");
      return;
    }

    setSaving(true);
    try {
      const payload = { name: form.name.trim(), onTime: form.onTime, offTime: form.offTime };
      let response;
      if (editingId) {
        response = await api.put(`${periodsBasePath}/${editingId}`, payload);
      } else {
        response = await api.post(periodsBasePath, payload);
      }
      onPeriodsChange(response.data.data);
      closeForm();
    } catch (err) {
      const message = err.response?.data?.message || "Erreur lors de l'enregistrement de la période";
      setError(message);
      // ✅ Le bandeau inline peut passer inaperçu si le formulaire n'est pas
      // entièrement visible (page chargée avec beaucoup de contenu à côté) —
      // on force une alerte explicite pour ne jamais laisser croire que
      // "rien ne s'est passé" alors que la période a été refusée à juste titre
      // (ex: chevauchement avec une période existante).
      alert(`⚠️ Période non enregistrée : ${message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (period) => {
    if (period.isLegacy) return; // rien à supprimer côté serveur, c'est un affichage synthétique
    if (!window.confirm(`Supprimer la période "${period.name}" ?`)) return;

    try {
      const response = await api.delete(`${periodsBasePath}/${period._id}`);
      onPeriodsChange(response.data.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="border-t pt-4">
      {/* Header + interrupteur maître */}
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold text-gray-800 flex items-center gap-2">
          📅 Programmation horaire
        </h3>
        <button
          type="button"
          disabled={readOnly || togglingEnabled}
          onClick={handleToggleEnabled}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
            scheduleEnabled ? 'bg-blue-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
              scheduleEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        {scheduleEnabled
          ? 'La climatisation ne s\'allume automatiquement que pendant les périodes ci-dessous.'
          : 'Programmation désactivée — l\'automatisation par présence fonctionne à toute heure.'}
      </p>

      {scheduleEnabled && (
        <>
          {/* Timeline 24h */}
          {displayPeriods.length > 0 && (
            <div className="mb-4">
              <div className="relative h-3 w-full rounded-full bg-gray-100 overflow-hidden">
                {displayPeriods.filter(p => p.enabled !== false).flatMap((p) =>
                  periodToTimelineSegments(p).map((seg, i) => (
                    <div
                      key={`${p._id || p.name}-${i}`}
                      className={`absolute top-0 h-full bg-gradient-to-r ${styleForTime(p.onTime).gradient}`}
                      style={{ left: `${seg.left}%`, width: `${seg.width}%` }}
                      title={`${p.name}: ${p.onTime} → ${p.offTime}`}
                    />
                  ))
                )}
              </div>
              <div className="flex justify-between text-[10px] text-gray-400 mt-1 font-mono">
                <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span>
              </div>
            </div>
          )}

          {/* Cartes de périodes */}
          <div className="space-y-2">
            {displayPeriods.map((period) => {
              const style = styleForTime(period.onTime);
              return (
                <div
                  key={period._id || 'legacy'}
                  className={`flex items-center justify-between rounded-xl border ${style.border} ${style.bg} px-4 py-3`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{style.icon}</span>
                    <div>
                      <p className={`font-semibold ${style.text}`}>
                        {period.name}
                        {period.enabled === false && (
                          <span className="ml-2 text-[10px] font-normal text-gray-400 uppercase">Désactivée</span>
                        )}
                      </p>
                      <p className="text-sm text-gray-600 font-mono">{period.onTime} → {period.offTime}</p>
                    </div>
                  </div>
                  {!readOnly && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openEditForm(period)}
                        className="text-sm font-medium text-blue-primary hover:underline"
                      >
                        Modifier
                      </button>
                      {!period.isLegacy && (
                        <button
                          type="button"
                          onClick={() => handleDelete(period)}
                          className="text-sm font-medium text-red-500 hover:underline"
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {displayPeriods.length === 0 && (
              <p className="text-sm text-gray-400 italic py-2">Aucune période configurée.</p>
            )}
          </div>

          {/* Formulaire ajout/édition — ⚠️ volontairement un <div>, pas un
              <form> : ce composant est parfois rendu à l'intérieur du
              <form> "Modifier l'association" (Admin/Owner). Un <form> imbriqué
              dans un autre est invalide en HTML et le navigateur finit par
              soumettre le formulaire PARENT (rechargement de page complet)
              au lieu de celui-ci — c'était la cause exacte du bug "Créer/
              Modifier/Supprimer ne fait rien, la page recharge /associations"
              côté Admin/Owner (Simple n'a pas de <form> englobant, d'où
              l'absence du problème là-bas). */}
          {!readOnly && (
            formOpen ? (
              <div className="mt-3 rounded-xl border-2 border-blue-200 bg-blue-50 p-4 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nom de la période</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                    placeholder="Ex: Matin, Après-midi, Soir..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-primary"
                    autoFocus
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Heure ON</label>
                    <input
                      type="time"
                      value={form.onTime}
                      onChange={(e) => setForm({ ...form, onTime: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Heure OFF</label>
                    <input
                      type="time"
                      value={form.offTime}
                      onChange={(e) => setForm({ ...form, offTime: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-primary"
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    ⚠️ {error}
                  </p>
                )}

                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 bg-blue-primary text-white text-sm font-semibold rounded-lg py-2 hover:opacity-90 disabled:opacity-50"
                  >
                    {saving ? 'Enregistrement...' : editingId ? 'Enregistrer' : 'Ajouter la période'}
                  </button>
                  <button
                    type="button"
                    onClick={closeForm}
                    className="px-4 text-sm font-medium text-gray-600 hover:text-gray-800"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={openAddForm}
                className="mt-3 w-full border-2 border-dashed border-gray-300 rounded-xl py-3 text-sm font-medium text-gray-500 hover:border-blue-primary hover:text-blue-primary transition"
              >
                ＋ Ajouter une période
              </button>
            )
          )}
        </>
      )}
    </div>
  );
};

export default SchedulePeriodsManager;
